import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { Button, TextField, Typography, Box, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import { Link } from '@mui/material';
import { useResponsive } from '../../helper/use-reponsive';
import { updatePassword, userLogin, verifyOtp } from '../../helper/api/api';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ForgetPassword = (props) => {
  const { handleEmailPassword, email, setEmail } = props;
  const upLg = useResponsive('up', 'lg');
  const [step, setStep] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [ipAddress, setIpAddress] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { Email: props?.email }
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const onSubmit = (data) => {
    const isMobileDevice = () => {
    const ua = navigator.userAgent.toLowerCase();
    const smallScreen = window.innerWidth < 768;
    const isMobileUA = /android|iphone|ipad|mobile|ipod/.test(ua);
      return isMobileUA || smallScreen;
    };
    if (isMobileDevice()) {
      toast.error("Mobile login is restricted.");
      return;
    }
    setIsLoading(true);
    const postData = { Email: data?.Email, Mobile: "", Password: "", IP: ipAddress };
    setEmail(data?.Email);

    userLogin(postData)
      .then((response) => {
        if (response?.data?.data?.Status === "Failed") {
          toast.error(response?.data?.Message || response?.data?.message, { duration: 4000 });
        } else {
          toast.success(response?.data?.data?.Message || response?.data?.data?.message, { duration: 4000 });
          setCountdown(95);
          reset({ Otp: "" });
          setStep('otp');
        }
      })
      .catch((error) => {
        toast.error(error?.data?.Message || error?.data?.data?.message, { duration: 4000 });
      })
      .finally(() => setIsLoading(false));
  };

  const handleResendOTP = () => {
    setIsLoading(true);
    const postData = { Email: email, Mobile: "", Password: "", IP: ipAddress };
    userLogin(postData)
      .then((response) => {
        if (response?.data?.data?.Status === "Failed") {
          toast.error(response?.data?.Message || response?.data?.message, { duration: 4000 });
        } else {
          toast.success('OTP Resent Successfully!', { duration: 4000 });
          setCountdown(95);
          reset({ Otp: "" });
        }
      })
      .catch((error) => {
        toast.error(error?.data?.Message || error?.data?.message, { duration: 4000 });
      })
      .finally(() => setIsLoading(false));
  };

const onSubmitOTP = async () => {
  if (otpValue.length !== 6) {
    toast.error("Please enter 6-digit OTP", { duration: 4000 });
    return;
  }
  setIsLoading(true);
  const postData = { Email: email, Mobile: "", OTP: otpValue, ISLogin: 0, IP: ipAddress };
  try {
    const response = await verifyOtp(postData);
    const message = response?.data?.message;
    if (message === 'Invalid Email OTP' || message === 'OTP was expired') {
      toast.error(message, { duration: 4000 });
    } else if (message) {
      toast.success(message, { duration: 4000 });
      reset({ NewPassword: '', ConfirmPassword: '' }); // ✅ clear previous fields
      setOtpValue(''); // ✅ clear OTP value too
      setStep('resetPassword'); // move to reset screen
    }
  } catch (error) {
    console.error("OTP submission error:", error);
    toast.error(error?.response?.data?.message, { duration: 4000 });
  } finally {
    setIsLoading(false);
  }
};


  const onSubmitConfirmPassword = (data) => {
    setIsLoading(true);
    const postData = {
      Email: email,
      Mobile: "",
      NewPassword: data?.NewPassword,
      ConfirmPassword: data?.ConfirmPassword,
    };

    if (postData.NewPassword !== postData.ConfirmPassword) {
      toast.error('Passwords do not match!', { duration: 2000 });
      setIsLoading(false);
      return;
    }
    if (postData.NewPassword.length < 6) {
      toast.error('Password must be at least 6 characters long!', { duration: 2000 });
      setIsLoading(false);
      return;
    }

    updatePassword(postData)
      .then((response) => {
        toast.success(response?.data?.Message || 'Password reset successful!', { duration: 2000 });
        handleEmailPassword();
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || 'Something went wrong!', { duration: 5000 });
      })
      .finally(() => setIsLoading(false));
  };

  const renderFormContent = () => {
    switch (step) {
      case 'email':
        return (
          <form method="post" action="#" onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="h5" gutterBottom>
              Forgot Password
            </Typography>
            <Typography variant="body2" gutterBottom>
              Enter your registered email address to receive the OTP.
            </Typography>
            <TextField
              label="Email"
              type="email"
              size='small'
              fullWidth
              margin="normal"
              {...register('Email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
                onChange: (e) => setEmail(e.target.value),
              })}
              error={!!errors.Email}
              helperText={errors.Email?.message}
            />
            <Box display='flex' pb={1} justifyContent='end'>
              <Link
                color='primary'
                sx={{ cursor: 'pointer', fontSize: upLg ? "0.75rem" : "0.6rem" }}
                onClick={props.handleEmailPassword}
              >
                Back
              </Link>
            </Box>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} />}
              sx={{ height: '30px', fontSize: '12px' }}
            >
              {isLoading ? 'Sending...' : 'Send OTP'}
            </Button>
          </form>
        );

      case 'otp':
        return (
          <form method="post" action="#" onSubmit={handleSubmit(onSubmitOTP)}>
            <Typography variant="h5" gutterBottom>
              Enter OTP
            </Typography>
            <Typography variant="body2" gutterBottom>
              OTP has been sent to: <strong>{email}</strong>
            </Typography>

            <TextField
              label="Enter 6-digit OTP"
              value={otpValue}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtpValue(val);
              }}
              fullWidth
              margin="normal"
              size='small'
              inputProps={{ maxLength: 6 }}
            />

            <Box display='flex' pb={1} justifyContent='end'>
              <Link color='primary' sx={{ cursor: 'pointer', fontSize: upLg ? "0.75rem" : "0.6rem" }} onClick={props.handleEmailPassword}>Back</Link>
            </Box>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </Button>

            <Box display="flex" justifyContent="center" mt={2}>
              {countdown > 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Resend OTP in {countdown} seconds
                </Typography>
              ) : (
                <Link color="primary" sx={{ cursor: 'pointer' }} onClick={handleResendOTP}>
                  Resend OTP
                </Link>
              )}
            </Box>
          </form>
        );

      case 'resetPassword':
        return (
          <form method="post" action="#" onSubmit={handleSubmit(onSubmitConfirmPassword)}>
            <Typography variant="h5" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" gutterBottom>
              Enter a new password for your account.
            </Typography>
            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              size='small'
              {...register('NewPassword', { required: 'New password is required' })}
              error={!!errors.NewPassword}
              helperText={errors.NewPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} sx={{ width: 32, height: 32, padding: 0 }}>
                      {showPassword ? <Visibility sx={{ fontSize: 18 }} /> : <VisibilityOff sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              size='small'
              {...register('ConfirmPassword', { required: 'Confirm password is required' })}
              error={!!errors.ConfirmPassword}
              helperText={errors.ConfirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} sx={{ width: 32, height: 32, padding: 0 }}>
                      {showPassword ? <Visibility sx={{ fontSize: 18 }} /> : <VisibilityOff sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box display='flex' pb={1} justifyContent='end'>
              <Link color='primary' sx={{ cursor: 'pointer', fontSize: upLg ? "0.75rem" : "0.6rem" }} onClick={props.handleEmailPassword}>Back</Link>
            </Box>

            <Button
              variant="contained"
              type="submit"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    const getLocalIP = () => {
      const peerConnection = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });

      peerConnection.createDataChannel('');
      peerConnection.createOffer().then((offer) => peerConnection.setLocalDescription(offer));

      peerConnection.onicecandidate = (event) => {
        if (event && event.candidate) {
          const candidate = event.candidate.candidate;
          const ipMatch = candidate.match(/([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3})/);
          if (ipMatch && ipMatch[1]) {
            setIpAddress(ipMatch[1]);
            peerConnection.close();
          }
        }
      };
    };
    getLocalIP();
  }, []);

  return (
    <Box className='block pt-20 w-11/12 pb-20'>
      <Box className="form-card">{renderFormContent()}</Box>
    </Box>
  );
};

export default ForgetPassword;
